import { useEffect, useState } from 'react';
import { api } from '../../api';
import type { Pack } from '../../api/types';
import { PREFIXO_FRAGMENTO_PENTA, PREFIXO_ORBE_PENTA, type Penta, type PentaEstado } from '../../api/evento';
import { numero, t } from '../../i18n/idioma';
import { CORES_PENTA, COR_HEX } from '../../world/pentagrama';
import { Abas } from '../kit';
import { ItemIcon } from '../ItemIcon';
import { Selo } from './Selo';
import { formatar, formatarLongo, restanteDe } from './tempo';

export type AbaPenta = 'recompensas' | 'orbes';

interface Props {
  penta: Penta | null;
  /** O estado do hub: mais novo que o GET, manda na fase e no relógio. */
  barra: PentaEstado | null;
  abaInicial: AbaPenta | 'como';
}

const qtd = (pack: Pack | null, defId: string) =>
  (pack?.items ?? []).filter(i => i.defId === defId && !i.equippedSlot).reduce((s, i) => s + i.qty, 0);

/**
 * O detalhe do Despertar da Ponta em Mais › Eventos (techspec-pentagrama §09), no molde do Chefe do Mundo: a ponta
 * da vez (ou ao vivo), e as abas Recompensas e Orbes (o caminho até o Shroud Robe). Os fragmentos ganhos chegam pelo
 * correio (techspec-correio §02). As regras vêm do servidor; o resto fica na Wiki.
 */
export function Pentagrama({ penta, barra, abaInicial }: Props) {
  const [aba, setAba] = useState<AbaPenta>(abaInicial === 'como' ? 'recompensas' : abaInicial);
  return (
    <>
      <div className="ev-heroi ev-heroi--compacto">
        <Selo id="penta" />
        <div>
          <h3>{t('penta.nome')}</h3>
          <p>{t('penta.resumo')}</p>
        </div>
      </div>
      <Acao penta={penta} barra={barra} />
      <Abas
        forma="sublinhada"
        valor={aba}
        onMuda={id => setAba(id as AbaPenta)}
        aria-label={t('penta.nome')}
        itens={[
          { id: 'recompensas', rotulo: t('evento.aba.recompensas') },
          { id: 'orbes', rotulo: t('penta.aba.orbes') }
        ]}
      />
      <div className="ev-aba ev-aba--compacta">
        {!penta && <p className="ev-vazio">{t('penta.carregando')}</p>}
        {penta && aba === 'recompensas' && <Recompensas penta={penta} />}
        {penta && aba === 'recompensas' && <p className="ev-dica ev-dica--correio">{t('evento.premio.correio')}</p>}
        {penta && aba === 'orbes' && <Orbes penta={penta} />}
      </div>
    </>
  );
}

function Acao({ penta, barra }: { penta: Penta | null; barra: PentaEstado | null }) {
  const ao = penta?.aoVivo ?? null;
  const corIdx = barra && barra.cor >= 0 && barra.fase < 2 ? barra.cor : ao ? CORES_PENTA.indexOf(ao.cor as never) : -1;
  if (corIdx >= 0) {
    const cor = CORES_PENTA[corIdx];
    const fase = barra?.fase ?? ao?.fase ?? 0;
    const guardiaoEm = barra?.guardiaoEmMs ?? ao?.guardiaoEmMs ?? 0;
    const fim = barra?.fimEmMs ?? ao?.fimEmMs ?? 0;
    return (
      <div className="ev-acao penta-acao" style={{ ['--cor' as string]: COR_HEX[cor] }}>
        <div className="ev-acao__l">
          <span className="ev-vivo">{t('evento.quadro.aoVivo')}</span>
          <b className="penta-acao__ponta">{t(`penta.ponta.${cor}`)}</b>
          <span className="ev-relogio" title={t('penta.det.fim')}>{formatar(restanteDe(fase === 0 ? guardiaoEm : fim))}</span>
        </div>
        <small className="ev-dica">{fase === 0 ? t('penta.det.ondas') : t('penta.det.guardiao', { nome: t(`penta.guardiao.${cor}`) })}</small>
        {ao && (
          <p className="ev-dica">
            {ao.meuDano >= ao.danoMinimo
              ? t('penta.det.conta', { d: numero(ao.meuDano) })
              : t('penta.det.falta', { d: numero(ao.meuDano), min: numero(ao.danoMinimo) })}
          </p>
        )}
        <p className="ev-dica">{t('penta.det.onde')}</p>
      </div>
    );
  }
  const prox = penta?.proximos.find(p => p.emMs > Date.now()) ?? null;
  return (
    <div className="ev-acao ev-acao--linha" title={t('penta.det.dicaAgenda', { a: Math.round((penta?.regras.avisoSegundos ?? 900) / 60) })}>
      <small>{t('evento.det.proximo')}</small>
      {prox ? (
        <b className="ev-acao__quando">
          {quando(prox.emMs, prox.hora)}
          <span> · <span className="ponto-cor" style={{ color: COR_HEX[prox.cor as keyof typeof COR_HEX] }}>●</span> {t(`penta.ponta.${prox.cor}`)}</span>
          {restanteDe(prox.emMs) < 86_400 && <span> · {formatarLongo(restanteDe(prox.emMs))}</span>}
        </b>
      ) : <b className="ev-acao__quando">{t('evento.chip.semHorario')}</b>}
    </div>
  );
}

function quando(emMs: number, hora: string) {
  const d = new Date(emMs);
  const hoje = new Date();
  const amanha = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + 1);
  if (d.toDateString() === hoje.toDateString()) return t('penta.det.hoje', { hora });
  if (d.toDateString() === amanha.toDateString()) return t('penta.det.amanha', { hora });
  return t('penta.det.dia', { dia: d.toLocaleDateString(undefined, { weekday: 'long' }), hora });
}

function Recompensas({ penta }: { penta: Penta }) {
  const r = penta.regras;
  return (
    <>
      <table className="penta-tab">
        <tbody>
          <tr><td>{t('penta.recomp.participar', { d: numero(r.danoMinimo) })}</td><td>+{r.fragmentosParticipar}</td></tr>
          <tr><td>{t('penta.recomp.guardiao')}</td><td>+{r.fragmentosGuardiao}</td></tr>
          <tr><td>{t('penta.recomp.top3')}</td><td>+{r.fragmentosTop3}</td></tr>
        </tbody>
      </table>
      <p className="ev-dica">{t('penta.recomp.regra')}</p>
      <h4 className="ev-sec__t">{t('penta.recomp.proximas')}</h4>
      <ul className="penta-prox">
        {penta.proximos.map(p => (
          <li key={p.emMs} style={{ ['--cor' as string]: COR_HEX[p.cor as keyof typeof COR_HEX] }}>
            <ItemIcon id={PREFIXO_FRAGMENTO_PENTA + p.cor} size={26} label={t(`penta.fragmento.${p.cor}`)} />
            <span>{quando(p.emMs, p.hora)}</span>
            <b>{t(`penta.ponta.${p.cor}`)}</b>
          </li>
        ))}
      </ul>
    </>
  );
}

function Orbes({ penta }: { penta: Penta }) {
  const [pack, setPack] = useState<Pack | null>(null);
  useEffect(() => {
    let vivo = true;
    void api.getPack().then(p => { if (vivo) setPack(p); }).catch(() => {});
    return () => { vivo = false; };
  }, []);
  const r = penta.regras;
  return (
    <>
      <div className="penta-orbes">
        {CORES_PENTA.map(c => {
          const frag = qtd(pack, PREFIXO_FRAGMENTO_PENTA + c);
          const orbe = qtd(pack, PREFIXO_ORBE_PENTA + c);
          const pct = orbe > 0 ? 100 : Math.min(100, (frag / r.fragmentosPorOrbe) * 100);
          return (
            <div key={c} className="penta-orbe" style={{ ['--cor' as string]: COR_HEX[c] }}>
              <ItemIcon id={(orbe > 0 ? PREFIXO_ORBE_PENTA : PREFIXO_FRAGMENTO_PENTA) + c} size={30} label={t(`penta.orbe.${c}`)} />
              <div>
                <b>{t(`penta.orbe.${c}`)}</b>
                <span className="penta-orbe__bar"><i style={{ width: `${pct}%` }} /></span>
                <small className="ev-mono">
                  {orbe > 0 ? t('penta.orbes.tenho', { n: orbe }) : t('penta.fragmentosDe', { n: frag, de: r.fragmentosPorOrbe })}
                </small>
              </div>
            </div>
          );
        })}
      </div>
      <p className="ev-dica">{t('penta.orbes.forja', { n: r.fragmentosPorOrbe, p: numero(r.prataPorOrbe) })}</p>
      <div className="penta-robe">
        <img src="/sprites/itens/traje/shroud.png" alt="" draggable={false} />
        <div>
          <b>{t('traje.peca.shroud')}</b>
          <small>{penta.temRobe ? t('penta.orbes.temRobe') : t('penta.orbes.ritual', { p: numero(r.prataRitual), b: r.robeBonus })}</small>
        </div>
      </div>
      {penta.mural.length > 0 && (
        <>
          <h4 className="ev-sec__t">{t('penta.mural')}</h4>
          <ol className="penta-mural">
            {penta.mural.slice(0, 10).map(m => (
              <li key={`${m.nome}-${m.emMs}`}><span>{m.nome}</span><small>{new Date(m.emMs).toLocaleDateString()}</small></li>
            ))}
          </ol>
        </>
      )}
    </>
  );
}
